import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getYearGroupLabel } from "../../Constant/ConstantData";
import { formatWeekCommencingDate } from "../../Util/HelperFunction";

export const DEFAULT_YEAR_COLORS = {
  year1: "#550C1E",
  year2: "#FFE113",
  year3: "#F58231",
  year4: "#8F3BA9",
  year5: "#3CB44B",
  year6: "#FFA1D2",
  year7: "#F032E6",
  year8: "#95E600",
  year9: "#FF003E",
  year10: "#FBAE43",
  year11: "#239895",
  year12: "#47A1E7",
  year13: "#FFA1D8",
  year14: "#47A1E7",
  year15: "#FFA1D8",
  year16: "#C40DFF",
  year17: "#C40DFF",
  year18: "#C40DFF",
  college_1st_year: "#47A1E7",
  college_2nd_year: "#FFA1D8",
  post_18: "#C40DFF",
  parent: "#249a95",
};

export const NUMERIC_YEAR_INFO = {
  1: { slug: "year_1", label: "Year 1", order: 1 },
  2: { slug: "year_2", label: "Year 2", order: 2 },
  3: { slug: "year_3", label: "Year 3", order: 3 },
  4: { slug: "year_4", label: "Year 4", order: 4 },
  5: { slug: "year_5", label: "Year 5", order: 5 },
  6: { slug: "year_6", label: "Year 6", order: 6 },
  7: { slug: "year_7", label: "Year 7", order: 7 },
  8: { slug: "year_8", label: "Year 8", order: 8 },
  9: { slug: "year_9", label: "Year 9", order: 9 },
  10: { slug: "year_10", label: "Year 10", order: 10 },
  11: { slug: "year_11", label: "Year 11", order: 11 },
  12: { slug: "college_1st_year", label: "College (1st Year)", order: 12 },
  13: { slug: "college_2nd_year", label: "College (2nd Year)", order: 13 },
  14: { slug: "college_1st_year", label: "College (1st Year)", order: 12 },
  15: { slug: "college_2nd_year", label: "College (2nd Year)", order: 13 },
  16: { slug: "post_18", label: "Post-18", order: 14 },
  17: { slug: "post_18", label: "Post-18", order: 14 },
  18: { slug: "post_18", label: "Post-18", order: 14 },
};

export const getSeriesMeta = (yearKey, colorsList = []) => {
  if (!yearKey) {
    return { name: "", color: "#666", order: 999 };
  }

  // 1. Parent
  if (yearKey === "parent" || yearKey === "parents") {
    const parentItem = Array.isArray(colorsList)
      ? colorsList.find(
          (c) =>
            c?.year === "parent" ||
            c?.name?.toLowerCase() === "parent" ||
            c?.name?.toLowerCase() === "parents"
        )
      : null;
    return {
      name: "Parents",
      color: parentItem?.color || DEFAULT_YEAR_COLORS.parent || "#249a95",
      order: 999,
    };
  }

  const cleanKey = String(yearKey).trim();
  const numMatch = cleanKey.match(/^year_?(\d+)$/i) || cleanKey.match(/^(\d+)$/);
  const num = numMatch ? parseInt(numMatch[1], 10) : null;

  // 2. Numeric match (e.g. year1, year15, 15)
  if (num !== null && NUMERIC_YEAR_INFO[num]) {
    const info = NUMERIC_YEAR_INFO[num];
    const matched = Array.isArray(colorsList)
      ? colorsList.find(
          (c) =>
            c?.year?.toLowerCase() === info.slug.toLowerCase() ||
            c?.name?.trim().toLowerCase() === info.label.toLowerCase() ||
            (c?.order !== undefined && Number(c.order) === info.order)
        )
      : null;

    return {
      name: matched?.name || info.label,
      color:
        matched?.color ||
        DEFAULT_YEAR_COLORS[cleanKey] ||
        DEFAULT_YEAR_COLORS[info.slug] ||
        DEFAULT_YEAR_COLORS[`year${num}`] ||
        "#666",
      order: matched?.order !== undefined ? Number(matched.order) : info.order,
    };
  }

  // 3. Slug or exact name match
  const stripStr = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const strippedKey = stripStr(cleanKey);

  const matchedBySlug = Array.isArray(colorsList)
    ? colorsList.find(
        (c) =>
          stripStr(c?.year) === strippedKey ||
          stripStr(c?.name) === strippedKey
      )
    : null;

  if (matchedBySlug) {
    return {
      name: matchedBySlug.name || getYearGroupLabel(cleanKey) || cleanKey,
      color: matchedBySlug.color || DEFAULT_YEAR_COLORS[cleanKey] || "#666",
      order: matchedBySlug.order !== undefined ? Number(matchedBySlug.order) : 500,
    };
  }

  // 4. Fallback via getYearGroupLabel
  const rawNum = cleanKey.replace(/^year_?/i, "");
  const label =
    getYearGroupLabel(rawNum) || getYearGroupLabel(cleanKey) || cleanKey;

  const matchedByName = Array.isArray(colorsList)
    ? colorsList.find(
        (c) => c?.name?.trim().toLowerCase() === label.trim().toLowerCase()
      )
    : null;

  return {
    name: matchedByName?.name || label,
    color:
      matchedByName?.color ||
      DEFAULT_YEAR_COLORS[cleanKey] ||
      DEFAULT_YEAR_COLORS[`year${rawNum}`] ||
      "#666",
    order: matchedByName?.order !== undefined ? Number(matchedByName.order) : 500,
  };
};

const MultiLineRegistrationChart = ({
  registerData,
  registerColors = [],
  colors: propColors,
  userData,
}) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (!Array.isArray(entries) || !entries.length) return;
      const entry = entries[0];
      setWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  const actualData = useMemo(() => {
    if (Array.isArray(registerData)) return registerData;
    if (Array.isArray(registerData?.data)) return registerData.data;
    return [];
  }, [registerData]);

  const apiColors = useMemo(() => {
    if (Array.isArray(registerColors) && registerColors.length > 0) {
      return registerColors;
    }
    if (Array.isArray(propColors) && propColors.length > 0) {
      return propColors;
    }
    if (Array.isArray(registerData?.colors) && registerData.colors.length > 0) {
      return registerData.colors;
    }
    return [];
  }, [registerColors, propColors, registerData]);

  const yearKeys = useMemo(() => {
    if (!actualData || actualData.length === 0) return [];

    const ignoredKeys = new Set([
      "date",
      "total",
      "_id",
      "createdAt",
      "updatedAt",
      "__v",
      "id",
    ]);

    const isYearOrRoleKey = (key) => {
      if (ignoredKeys.has(key)) return false;
      const lower = key.toLowerCase();
      return (
        lower.startsWith("year") ||
        lower === "parent" ||
        lower === "parents" ||
        lower.includes("college") ||
        lower.includes("post") ||
        !isNaN(Number(key))
      );
    };

    const keys = [
      ...new Set(
        actualData.flatMap((item) =>
          Object.keys(item).filter((key) => isYearOrRoleKey(key))
        )
      ),
    ];

    return keys.sort((a, b) => {
      const metaA = getSeriesMeta(a, apiColors);
      const metaB = getSeriesMeta(b, apiColors);
      return metaA.order - metaB.order;
    });
  }, [actualData, apiColors]);

  const processedData = useMemo(() => {
    if (!actualData || actualData.length === 0) return [];

    const dataWithAllYears = actualData.map((item) => {
      const processedItem = { ...item };
      yearKeys.forEach((yearKey) => {
        if (!(yearKey in processedItem)) {
          processedItem[yearKey] = 0;
        }
      });
      return processedItem;
    });

    const cumulativeData = [];
    const runningTotals = {};

    yearKeys.forEach((yearKey) => {
      runningTotals[yearKey] = 0;
    });

    dataWithAllYears.forEach((item) => {
      const cumulativeItem = { ...item };

      yearKeys.forEach((yearKey) => {
        runningTotals[yearKey] += Number(item[yearKey]) || 0;
        cumulativeItem[yearKey] = runningTotals[yearKey];
      });

      cumulativeData.push(cumulativeItem);
    });

    return cumulativeData;
  }, [actualData, yearKeys]);

  const maxVal = useMemo(() => {
    if (!processedData || processedData.length === 0) return 15;
    let max = 0;
    processedData.forEach((item) => {
      yearKeys.forEach((key) => {
        const val = Number(item[key]);
        if (!isNaN(val) && val > max) {
          max = val;
        }
      });
    });
    return max;
  }, [processedData, yearKeys]);

  const yAxisTicks = useMemo(() => {
    let limit = maxVal || 5;
    let step = 1;

    if (limit <= 5) {
      limit = Math.max(4, limit + 1);
      step = 1;
    } else if (limit <= 10) {
      limit = Math.ceil(limit / 2) * 2;
      step = 2;
    } else if (limit <= 20) {
      limit = Math.ceil(limit / 5) * 5;
      step = 5;
    } else if (limit <= 50) {
      limit = Math.ceil(limit / 10) * 10;
      step = 10;
    } else if (limit <= 100) {
      limit = Math.ceil(limit / 20) * 20;
      step = 20;
    } else if (limit <= 200) {
      limit = Math.ceil(limit / 50) * 50;
      step = 50;
    } else if (limit <= 500) {
      limit = Math.ceil(limit / 100) * 100;
      step = 100;
    } else if (limit <= 1000) {
      limit = Math.ceil(limit / 200) * 200;
      step = 200;
    } else {
      limit = Math.ceil(limit / 500) * 500;
      step = 500;
    }

    const ticks = [];
    for (let i = 0; i <= limit; i += step) {
      ticks.push(i);
    }
    return ticks;
  }, [maxVal]);

  const requirements = useMemo(() => {
    const N = processedData?.length || 0;
    if (N <= 1) {
      return { shouldScroll: false, chartWidth: "100%", interval: 0 };
    }
    const requiredWidth = (N - 1) * 75 + 110;
    const availableWidth = width || 500;
    const shouldScroll = availableWidth < requiredWidth;
    
    return {
      shouldScroll,
      chartWidth: shouldScroll ? `${requiredWidth}px` : "100%",
      interval: 0
    };
  }, [processedData?.length, width]);

  const renderTooltipContent = ({ payload, label }) => {
    if (payload && payload.length > 0) {
      const filteredPayload = payload.filter((item) => item.value !== 0);
      if (filteredPayload.length === 0) return null;

      const total = filteredPayload.reduce((sum, item) => sum + item.value, 0);

      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px 14px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            border: "1px solid #e0e5f2",
            fontFamily: "Raleway, sans-serif",
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: "6px", color: "#2B3674" }}>
            {formatWeekCommencingDate(label)}
          </div>
          {filteredPayload.map((item, index) => (
            <div
              key={index}
              style={{
                color: item.color,
                fontSize: "13px",
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
                padding: "2px 0",
              }}
            >
              <span>{item.name}:</span>
              <strong>{item.value}</strong>
            </div>
          ))}
          <div
            style={{
              borderTop: "1px solid #e0e5f2",
              marginTop: "6px",
              paddingTop: "6px",
              fontWeight: "bold",
              color: "#2B3674",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>Total:</span>
            <span>{total}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderLines = () => {
    return yearKeys.map((yearKey) => {
      const meta = getSeriesMeta(yearKey, apiColors);
      return (
        <Line
          key={yearKey}
          type="monotone"
          dataKey={yearKey}
          stroke={meta.color}
          name={meta.name}
          dot={{ r: 4, strokeWidth: 2, fill: "#ffffff", stroke: meta.color }}
          activeDot={{ r: 6, strokeWidth: 2, fill: meta.color }}
          strokeWidth={2.5}
          connectNulls={false}
        />
      );
    });
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          width: "100%",
          overflowX: requirements.shouldScroll ? "auto" : "visible",
          overflowY: "hidden",
          boxSizing: "border-box",
        }}
        className="chart-scroll-container"
      >
        <div
          style={{
            width: requirements.chartWidth,
            minWidth: "100%",
            height: 350,
            boxSizing: "border-box",
          }}
        >
          <ResponsiveContainer width="100%" height={350}>
            <LineChart
              data={processedData}
              margin={{
                top: 15,
                right: 30,
                left: 65,
                bottom: 30,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatWeekCommencingDate}
                tick={{ fontSize: 11, fill: "#707EAE" }}
                angle={-45}
                textAnchor="end"
                height={95}
                dx={-4}
                dy={6}
                interval={requirements.interval}
                padding={{ left: 0, right: 0 }}
              />
              <YAxis
                allowDecimals={false}
                ticks={yAxisTicks}
                domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                interval={0}
                tick={{ fontSize: 12, fill: "#707EAE" }}
                width={40}
              />
              <Tooltip
                labelFormatter={formatWeekCommencingDate}
                content={renderTooltipContent}
              />
              {renderLines()}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Legend below the chart */}
      {yearKeys.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "15px",
            marginTop: "15px",
          }}
        >
          {yearKeys.map((yearKey) => {
            const meta = getSeriesMeta(yearKey, apiColors);

            return (
              <div
                key={yearKey}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: meta.color,
                    borderRadius: "2px",
                  }}
                />
                <span style={{ fontSize: "12px", color: "#666" }}>
                  {meta.name}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
  .row {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    width: 100%;
  }
  .col-xxl-7,
  .col-xxl-5 {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .cmn-card-color.table-cell {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
  .details-chart {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }
  .chart-scroll-container {
    -webkit-overflow-scrolling: touch;
  }
  .chart-scroll-container::-webkit-scrollbar {
    height: 8px;
  }
  .chart-scroll-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  .chart-scroll-container::-webkit-scrollbar-thumb {
    background: #c3c3c3;
    border-radius: 4px;
  }
  .chart-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`}</style>
    </div>
  );
};

export default MultiLineRegistrationChart;
